import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Grid, Typography, Snackbar} from "@mui/material";
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import {useNavigate} from "react-router-dom";
import Bottom from "../Bottom";
import Top from "../Top";
import DeviceTypeIcon from "../../components/DeviceTypeIcon";
import historyStore, {selectedDevicesOf} from "../../store/historyStore";
import {MAX_DEVICE_COUNT, getDeviceById, getDeviceListByType} from "../../utils/deviceInfo";
import {registeredDeviceIds, saveHealthDevice, useHealthDevice} from "../../utils/healthDeviceApi";

const HistoryDevice = () => {
  const navigate = useNavigate();
  const storeData: any = historyStore();
  // 이전 계정의 선택으로 시작하면 그대로 저장했을 때 새 계정의 서버 등록을 덮어쓴다
  const [selected, setSelected] = useState<string[]>(() => selectedDevicesOf(storeData));
  const [alertMsg, setAlertMsg] = useState('');
  const [saving, setSaving] = useState(false); // 저장 응답 대기 중 연타 방지
  const deviceGroupList = getDeviceListByType();

  /**
   * 서버에 등록된 기기를 불러와 초기 선택으로 삼는다.
   *
   * 로컬 값으로 먼저 그려 두고(깜빡임 방지) 조회가 끝나면 서버 값으로 맞춘다.
   * AP_009_003 은 계정별로 저장하므로 서버가 정본이다 — 브라우저를 바꾸거나 스토리지를
   * 비우면 로컬은 비어 있지만 서버 등록은 남아 있다.
   *
   * 사용자가 화면에서 한 번이라도 고른 뒤에는 덮어쓰지 않는다(고르던 중에 되돌아가면
   * 무엇을 골랐는지 알 수 없게 된다). 서버 등록이 하나도 없으면 그대로 둔다 —
   * 서버 저장에 실패해 이 기기에만 남아 있는 선택을 지우지 않기 위해서다.
   */
  const { data: deviceData, loading: deviceLoading } = useHealthDevice(0);
  const serverKey = registeredDeviceIds(deviceData).join(',');
  const touchedRef = useRef(false);

  useEffect(() => {
    if (touchedRef.current || deviceLoading) return;
    if (serverKey === '') return;
    setSelected(serverKey.split(','));
  }, [serverKey, deviceLoading]);

  function toggleDevice(id: string) {
    const device = getDeviceById(id);
    if (device === undefined || device.excluded) {
      // 실증 제외 기기는 선택할 수 없음
      return;
    }
    touchedRef.current = true;
    if (selected.indexOf(id) > -1) {
      setSelected(selected.filter((v) => v !== id));
      return;
    }

    // 같은 분류는 1대만 선택 - 다른 기기를 고르면 기존 선택이 해제됨
    const sameTypeIndex = selected.findIndex((v) => {
      const selectedDevice = getDeviceById(v);
      return selectedDevice !== undefined && selectedDevice.type === device.type;
    });
    if (sameTypeIndex > -1) {
      const next = [...selected];
      next[sameTypeIndex] = id;
      setSelected(next);
      return;
    }

    if (selected.length >= MAX_DEVICE_COUNT) {
      setAlertMsg('기기는 최대 ' + MAX_DEVICE_COUNT + '개까지 선택할 수 있습니다.');
      return;
    }
    setSelected([...selected, id]);
  }

  /**
   * 선택한 기기를 저장한다.
   *
   * 예전에는 zustand(localStorage)에만 넣어서, 기기를 바꾸거나 앱을 지우면 선택이
   * 사라지고 서버는 사용자가 어떤 기기를 쓰는지 알지 못했다. AP_009_003 으로 함께 보낸다.
   *
   * 로컬 저장을 먼저 하는 이유: 서버 저장이 실패해도 이 기기에서의 선택은 남는다
   * (기존 동작과 같다). 실패하면 화면에 남아 다시 시도할 수 있게 한다.
   */
  async function saveDevice() {
    if (saving) return;
    setSaving(true);
    storeData.setSelectedDeviceList(selected);
    try {
      await saveHealthDevice(selected);
      navigate(-1);
    } catch (error) {
      console.log(error);
      setAlertMsg('서버에 저장하지 못했습니다. 선택은 이 기기에 저장되었습니다.');
      setSaving(false);
    }
  }

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title="기기 선택" />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>
          <Typography component="div" sx={{ fontSize: 16, color: '#555', m: '20px 0 4px 0', textAlign: 'center' }}>
            연동할 기기를 <b>최대 {MAX_DEVICE_COUNT}개</b>까지 선택해 주세요.
          </Typography>
          <Typography component="div" sx={{ fontSize: 13, color: '#888', mb: '10px', textAlign: 'center' }}>
            분류별로 1대만 선택할 수 있습니다.
          </Typography>
          <Typography component="div" sx={{ fontSize: 14, color: '#EF8322', mb: 2, textAlign: 'center', fontWeight: 700 }}>
            {selected.length} / {MAX_DEVICE_COUNT} 선택
          </Typography>

          {deviceGroupList.map((group) => (
            <Box key={group.type} sx={{ mb: 2, bgcolor: '#fff', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
              <Stack direction={'row'} alignItems={'center'} sx={{ p: '12px 15px' }} spacing={1}>
                <DeviceTypeIcon type={group.type} color={'#aaa'} />
                <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>{group.typeName}</Typography>
              </Stack>
              <Divider />

              {group.list.map((device, index) => {
                const isSelected = selected.indexOf(device.id) > -1;
                return (
                  <Box key={device.id}>
                    {index > 0 ? <Divider sx={{ ml: 2, mr: 2 }} /> : null}
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}
                      onClick={() => toggleDevice(device.id)}
                      sx={{ p: '5px 10px 5px 15px', cursor: device.excluded ? 'default' : 'pointer', bgcolor: device.excluded ? '#f7f7f7' : 'transparent' }}>
                      {/* minWidth 0 이 없으면 좁은 화면에서 긴 기기명이 체크박스를 밀어낸다 */}
                      <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ minWidth: 0 }}>
                        <Typography noWrap title={device.name} sx={{ fontSize: 15, color: device.excluded ? '#bbb' : (isSelected ? '#333' : '#666'), fontWeight: isSelected ? 700 : 500 }}>
                          {device.name}
                        </Typography>
                        {device.excluded ? (
                          <Typography sx={{ fontSize: 11, color: '#bbb', border: '1px solid #e0e0e0', borderRadius: '10px', p: '1px 6px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                            실증 제외
                          </Typography>
                        ) : null}
                      </Stack>
                      <Checkbox
                        checked={isSelected}
                        disabled={device.excluded}
                        icon={<RadioButtonUncheckedRoundedIcon sx={{ color: '#ccc' }} />}
                        checkedIcon={<CheckCircleRoundedIcon sx={{ color: '#EF8322' }} />}
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          ))}

          <Stack direction={'row'} spacing={1} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving} sx={CancelBtnStyle}>취소</Button>
            <Button variant="contained" onClick={saveDevice} disabled={saving} sx={BottomBtnStyle}>
              {saving ? '저장 중…' : '선택 완료'}
            </Button>
          </Stack>
        </Box>
      </Grid>

      <Snackbar
        open={alertMsg !== ''}
        autoHideDuration={2000}
        onClose={() => setAlertMsg('')}
        message={alertMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 90 } }}
      />

      <Bottom />
    </Stack>
  );
}

const BottomBtnStyle = {
  width: '50%',
  bgcolor: '#EF8322',
  fontSize: 18,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#fff',
  border: '1px solid #ef8322',
  ':hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color: '#fff'
  },
  ':active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  ':focus': {
    boxShadow: 'none',
  },
};

const CancelBtnStyle = {
  width: '50%',
  bgcolor: '#fff',
  fontSize: 18,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#888',
  border: '1px solid #ccc',
  ':hover': {
    backgroundColor: '#fff',
    borderColor: '#ccc',
    boxShadow: 'none',
  },
};

export default HistoryDevice;
